import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Form,
} from "react-bootstrap";
import { FaEye } from "react-icons/fa";
import { BiRupee } from "react-icons/bi";
import "./CustomerList.css";
import { Link } from "react-router-dom";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../Components/Pagination";
import axios from "axios";
import { Button } from "react-bootstrap";

const parsePossibleDate = (value) => {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return new Date(value);
  const parts = value.split("/");
  if (parts.length === 3) {
    const d = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10) - 1;
    const y = parseInt(parts[2], 10);
    const dt = new Date(y, m, d);
    return isNaN(dt) ? null : dt;
  }
  const dt = new Date(value);
  return isNaN(dt) ? null : dt;
};

const parseDateDMY = (value) => {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return new Date(value);
  const sep = value.includes("-") ? "-" : value.includes("/") ? "/" : null;
  if (sep) {
    const parts = value.split(sep);
    if (parts.length === 3 && parts[2].length === 4) {
      const d = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10) - 1;
      const y = parseInt(parts[2], 10);
      const dt = new Date(y, m, d);
      return isNaN(dt) ? null : dt;
    }
  }
  const dt = new Date(value);
  return isNaN(dt) ? null : dt;
};

const SalaryAided = () => {
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [salaryData, setSalaryData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.post(`${API_BASE_URL}salary_aided_list.php`)
      .then((response) => {
        if (response.data.head.code === 200) {
          const list = response.data.body;
          setSalaryData(list);
          console.log("Aided Salary Data:", list);
        } else {
          console.error("Error Fetching Data:", response.data.head.msg);
          setSalaryData([]);
        }
      })
      .catch((error) => {
        console.error("API Error:", error);
        setSalaryData([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // Calculate Gross, Deduction, and Net Salary
  const calculateSalary = (item) => {
    // Calculate Gross
    const gross = 
      (parseFloat(item.basic) || 0) +
      (parseFloat(item.sp) || 0) +
      (parseFloat(item.wa) || 0) +
      (parseFloat(item.da) || 0) +
      (parseFloat(item.hra) || 0) +
      (parseFloat(item.ma) || 0);

    // Calculate Deduction
    const deduction =
      (parseFloat(item.tpf) || 0) +
      (parseFloat(item.tpfd) || 0) +
      (parseFloat(item.cpsr) || 0) +
      (parseFloat(item.cpsa1) || 0) +
      (parseFloat(item.gis) || 0) +
      (parseFloat(item.nhis) || 0) +
      (parseFloat(item.faCoop) || 0) +
      (parseFloat(item.ptItSr) || 0);

    const netSalary = gross - deduction;

    return { 
      gross: gross.toFixed(2), 
      deduction: deduction.toFixed(2), 
      netSalary: netSalary.toFixed(2) 
    };
  };

  // Filter data with search and date range
  const filteredData = salaryData.filter((item) => {
    // Search filter - check name, designation, and department
    if (search) {
      const q = search.toLowerCase();
      const nameMatch = item.name && item.name.toLowerCase().includes(q);
      const desigMatch = item.desig_name && item.desig_name.toLowerCase().includes(q);
      const deptMatch = item.depart_name && item.depart_name.toLowerCase().includes(q);
      if (!nameMatch && !desigMatch && !deptMatch) return false;
    }
    
    // Date range filter
    if (fromDate || toDate) {
      const reqDate = parseDateDMY(item.added_date);
      if (!reqDate) return false;
      if (fromDate) {
        const from = new Date(fromDate + "T00:00:00");
        if (reqDate < from) return false;
      }
      if (toDate) {
        const to = new Date(toDate + "T23:59:59");
        if (reqDate > to) return false;
      }
    }
    return true;
  });

  // Pagination
  const {
    currentPage,
    totalPages,
    currentItems,
    handlePageChange,
    indexOfFirstItem,
  } = usePagination(filteredData, 25);

  const handleExport = () => {
    console.log("Exporting Aided Salary CSV", fromDate, toDate);
    if(fromDate =="" || toDate ==""){
      alert("Select Date Range !");
      return;
    }
    
    axios({
      url: `${API_BASE_URL}salary_aided_excel.php`,
      data: {
        from_date: fromDate,
        to_date: toDate
      },
      method: 'POST',
      responseType: 'blob',
    })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'salary_aided_sheet.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch((error) => {
        console.error("Error downloading CSV:", error);
      });
  };

  // Loading state
  if (loading) {
    return (
      <Container fluid className="my-4 outer-container">
        <div className="text-center">Loading...</div>
      </Container>
    );
  }

  return (
    <>
      <Container fluid className="my-4 outer-container">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <p></p>
          <h2 className="text-center">Aided Staff Salary</h2>
          <Button variant="success" onClick={handleExport}>
            Export to Excel
          </Button>
        </div>

        <Row className="mb-3">
          <Col md={4}>
            <Form.Label>Search</Form.Label>
            <Form.Control
              type="text"
              placeholder="Search by name, designation or department"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Col>
          <Col md={4}>
            <Form.Label>From Date</Form.Label>
            <Form.Control
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </Col>
          <Col md={4}>
            <Form.Label>To Date</Form.Label>
            <Form.Control
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </Col>
        </Row>

        <div className="table-pagination-wrapper">
          <div className="table-pagination-content">
            <Table bordered responsive className="table-striped customer-table">
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th>Name</th>
                  <th>Designation / Dept.</th>
                  <th>DOJ</th>
                  <th>Basic Salary</th>
                  <th>Total Days</th>
                  <th>LoP</th>
                  <th>Working Days</th>
                  <th>Gross</th>
                  <th>Deduction</th>
                  <th>Net Salary</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((item, i) => {
                  const calc = calculateSalary(item);
                  return (
                    <tr key={item.salary_id}>
                      <td>{currentItems.length - (indexOfFirstItem + i)}</td>
                      <td>{item.name || 'N/A'}</td>
                      <td>
                        {item.desig_name || 'N/A'} - {item.depart_name || 'N/A'}
                      </td>
                      <td>{item.date_of_join || 'N/A'}</td>
                      <td>
                        <BiRupee />
                        {item.basic || 0}
                      </td>
                      <td>{item.total_days || 0}</td>
                      <td>{item.lop || 0}</td>
                      <td>{item.working_days || 0}</td>
                      <td>
                        <BiRupee />
                        {item.gross}
                      </td>
                      <td>
                        <BiRupee />
                        {item.deduction}
                      </td>
                      <td>
                        <BiRupee />
                        {item.net_salary}
                      </td>
                      <td className="action-icons">
                        <Link to={`/viewsalaryaided/${item.salary_id}`}>
                          <FaEye className="view-icon" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}

                {currentItems.length === 0 && (
                  <tr>
                    <td colSpan="12" className="text-center">
                      No records found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>

        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </Container>
    </>
  );
};

export default SalaryAided;